import React from 'react';
import { Slider } from 'antd';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { MODULE_MAX_WEDGES, wedgeCountErrors, wedgeErrors } from '../../adapters/game';
import StepPanel from '../../kit/StepPanel';
import { RadioGroup } from '../../kit/controls';
import { LoyaltySelect, TextField } from '../../kit/fields';
import { Hint, Line, Sub, Words } from '../../kit/layout';
import { RewardPicker } from '../../kit/pickers';
import {
  IWizardStepProps,
  MAX_WEDGES,
  MIN_WEDGES,
  resizeWedges,
  WizardState,
  WizardWedge
} from '../../wizardModel';
import { wz, wzType } from '../../wizardStyles';

const TW = `${TOrbit}.wizard`;
const TH = `${TW}.game.wheel`;

// 6985:13262 `div.wnum`: the wedge colours, in wheel order; a wheel past 8 starts them again.
export const WEDGE_INK = [
  '#EC4899',
  '#F43F5E',
  '#F97316',
  '#F59E0B',
  '#22C55E',
  '#06B6D4',
  '#6D5DFC',
  '#268AFF'
];

const setWedges = (state: WizardState, wedges: WizardWedge[]): WizardState => ({
  ...state,
  game: { ...state.game, wedges }
});

const Card = styled.div`
  padding: 20px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
  background: ${wz.field};
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

const ValueChip = styled.span`
  padding: 4px 12px;
  border-radius: 8px;
  background: ${wz.panel};
  border: ${wz.line} solid ${wz.border};
  color: ${wz.text};
  font-weight: 700;
  ${wzType.input}
`;

const Ends = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${wz.muted};
  ${wzType.label}

  b {
    color: ${wz.text};
  }
`;

const Warning = styled.p`
  margin: 12px 0 0;
  color: ${wz.highlight};
  ${wzType.label}
`;

const useTitle = () => {
  const { t } = useMultiLanguageTranslation();
  return t(`${TW}.setup.game`, { game: t(`${TW}.game.spin_the_wheel`) });
};

/** 6985:12715, Step 3/5: how many wedges, 4 to 12. */
export const WedgeCountStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const heading = useTitle();
  const count = state.game.wedges.length;
  const marks = Object.fromEntries(
    Array.from({ length: MAX_WEDGES - MIN_WEDGES + 1 }, (_, index) => [MIN_WEDGES + index, ' '])
  );
  return (
    <StepPanel
      step="wedges"
      narrow
      title={heading}
      question={t(`${TH}.countQuestion`)}
      errors={wedgeCountErrors(count, t)}
      onNext={next}
      onBack={back}
    >
      <Hint>{t(`${TH}.countHint`)}</Hint>
      <Card style={{ marginTop: 18 }}>
        <CardHead>
          <div>
            <CardTitle>{t(`${TH}.countTitle`)}</CardTitle>
            <Hint>{t(`${TH}.countBody`)}</Hint>
          </div>
          <ValueChip data-testid="orbit-wizard-wedge-count">{count}</ValueChip>
        </CardHead>
        <Slider
          min={MIN_WEDGES}
          max={MAX_WEDGES}
          step={1}
          marks={marks}
          dots
          value={count}
          tooltip={{ open: false }}
          aria-label={t(`${TH}.countTitle`)}
          onChange={(value: number) =>
            update(current => setWedges(current, resizeWedges(current.game.wedges, value)))
          }
        />
        <Ends>
          <span>
            {`${t(`${TH}.min`)} `}
            <b>{t(`${TH}.wedges`, { n: MIN_WEDGES })}</b>
          </span>
          <span>
            {`${t(`${TH}.max`)} `}
            <b>{t(`${TH}.wedges`, { n: MAX_WEDGES })}</b>
          </span>
        </Ends>
        {count > MODULE_MAX_WEDGES ? <Warning role="note">{t(`${TH}.overModule`)}</Warning> : null}
      </Card>
    </StepPanel>
  );
};

const List = styled.div`
  max-height: 640px;
  margin-top: 18px;
  overflow-y: auto;
  ${intelligenceScrollbar('y')}

  > * + * {
    margin-top: 8px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 4px 14px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
  background: ${wz.field};

  > :first-child {
    margin: 9px 14px 0 0;
  }
`;

const WedgeNumber = styled.span<{ $ink: string }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background: ${({ $ink }) => $ink};
  color: #ffffff;
  font-weight: 700;
  ${wzType.label}
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

/** 6985:13262, Step 4/5: Points, A Reward or No Prize on every wedge. */
export const WedgeOutcomesStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const heading = useTitle();
  const { wedges } = state.game;
  const change = (index: number, patch: Partial<WizardWedge>) =>
    update(current =>
      setWedges(
        current,
        current.game.wedges.map((wedge, at) => (at === index ? { ...wedge, ...patch } : wedge))
      )
    );

  return (
    <StepPanel
      step="wedgeOutcomes"
      narrow
      title={heading}
      question={t(`${TH}.outcomesQuestion`)}
      errors={wedgeErrors(wedges, t)}
      onNext={next}
      onBack={back}
    >
      <Words>{t(`${TH}.outcomesTitle`)}</Words>
      <div>
        <Hint>{t(`${TH}.outcomesHint`, { n: wedges.length })}</Hint>
      </div>
      <List>
        {wedges.map((wedge, index) => {
          const n = index + 1;
          return (
            // Wedges are their position on the wheel.
            <Row key={index} data-testid="orbit-wizard-wedge">
              <WedgeNumber $ink={WEDGE_INK[index % WEDGE_INK.length]} aria-hidden="true">
                {n}
              </WedgeNumber>
              <Body>
                <Line>
                  <RadioGroup<WizardWedge['type']>
                    name={`orbit-wizard-wedge-${index}`}
                    label={t(`${TH}.wedge`, { n })}
                    value={wedge.type}
                    options={[
                      { value: 'points', label: t(`${TW}.prize.points`) },
                      { value: 'reward', label: t(`${TW}.prize.reward`) },
                      { value: 'none', label: t(`${TH}.noPrize`) }
                    ]}
                    onChange={type => change(index, { type })}
                  />
                </Line>
                {wedge.type === 'points' ? (
                  <Sub>
                    <Line>
                      <Words $muted>{t(`${TW}.prize.award`)}</Words>
                      <TextField
                        label={`${t(`${TH}.wedge`, { n })} ${t(`${TW}.prize.award`)}`}
                        placeholder="0"
                        inputMode="numeric"
                        value={wedge.points}
                        onChange={points => change(index, { points })}
                      />
                      <Words $muted>{t(`${TW}.prize.pointsFrom`)}</Words>
                      <LoyaltySelect
                        value={wedge.loyalty}
                        onChange={loyalty => change(index, { loyalty })}
                      />
                    </Line>
                  </Sub>
                ) : null}
                {wedge.type === 'reward' ? (
                  <Sub>
                    <Line>
                      <Words $muted>{t(`${TW}.prize.issue`)}</Words>
                      <RewardPicker
                        label={t(`${TW}.prize.selectReward`)}
                        value={wedge.reward}
                        onChange={reward => change(index, { reward })}
                      />
                    </Line>
                  </Sub>
                ) : null}
              </Body>
            </Row>
          );
        })}
      </List>
    </StepPanel>
  );
};
